import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Database,
  Download,
  Upload,
  Trash2,
  RefreshCw,
  Settings,
  Clock,
  HardDrive,
  Calendar,
  Check,
  AlertTriangle,
  Play,
  Save,
  X,
  FileJson,
  Shield,
  Zap,
} from "lucide-react";
import toast from "react-hot-toast";

const API = `${window.location.origin}/api`;

export default function BackupPage() {
  const [backups, setBackups] = useState([]);
  const [config, setConfig] = useState({
    auto_backup_enabled: false,
    backup_frequency: "daily",
    backup_time: "00:00",
    retention_days: 30,
    include_products: true,
    include_customers: true,
    include_suppliers: true,
    include_sales: true,
    include_purchases: true,
    include_expenses: true,
    include_employees: true,
    include_settings: true
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [backupsRes, configRes, statsRes] = await Promise.all([
        axios.get(`${API}/backup/list`),
        axios.get(`${API}/backup/config`),
        axios.get(`${API}/backup/stats`)
      ]);
      setBackups(backupsRes.data.backups || []);
      setConfig(configRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("حدث خطأ في جلب البيانات");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/backup/create`);
      toast.success(`تم إنشاء النسخة الاحتياطية! (${response.data.total_records} سجل)`);
      fetchData();
    } catch (error) {
      toast.error("حدث خطأ في إنشاء النسخة الاحتياطية");
    } finally {
      setCreating(false);
    }
  };

  const downloadBackup = async (backupId) => {
    try {
      window.open(`${API}/backup/download/${backupId}`, '_blank');
      toast.success("جاري تحميل النسخة الاحتياطية...");
    } catch (error) {
      toast.error("حدث خطأ في تحميل النسخة الاحتياطية");
    }
  };

  const deleteBackup = async (backupId) => {
    if (!window.confirm("هل أنت متأكد من حذف هذه النسخة الاحتياطية؟")) return;
    
    try {
      await axios.delete(`${API}/backup/${backupId}`);
      toast.success("تم حذف النسخة الاحتياطية");
      fetchData();
    } catch (error) {
      toast.error("حدث خطأ في حذف النسخة الاحتياطية");
    }
  };

  const saveConfig = async () => {
    try {
      await axios.post(`${API}/backup/config`, config);
      toast.success("تم حفظ الإعدادات");
      setShowSettings(false);
      fetchData();
    } catch (error) {
      toast.error("حدث خطأ في حفظ الإعدادات");
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString('ar-IQ');
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="backup-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
            <Database className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">النسخ الاحتياطي</h1>
            <p className="text-slate-400 text-sm">إدارة وحماية بياناتك</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={fetchData}
            disabled={loading}
            className="bg-slate-800 hover:bg-slate-700"
          >
            <RefreshCw className={`w-4 h-4 me-2 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </Button>
          <Button
            onClick={() => setShowSettings(!showSettings)}
            variant="outline"
            className="border-slate-600"
          >
            <Settings className="w-4 h-4 me-2" />
            الإعدادات
          </Button>
          <Button
            onClick={createBackup}
            disabled={creating}
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
          >
            {creating ? (
              <RefreshCw className="w-4 h-4 me-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 me-2" />
            )}
            إنشاء نسخة احتياطية
          </Button>
        </div>
      </div>

      {/* إحصائيات */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 border-blue-500/30">
          <CardContent className="p-4 text-center">
            <Database className="w-8 h-8 text-blue-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-blue-400">{stats?.total_backups || 0}</p>
            <p className="text-sm text-slate-400">النسخ المحفوظة</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border-purple-500/30">
          <CardContent className="p-4 text-center">
            <HardDrive className="w-8 h-8 text-purple-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-purple-400">{stats?.total_size_mb || 0} MB</p>
            <p className="text-sm text-slate-400">الحجم الإجمالي</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 border-emerald-500/30">
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <p className="text-lg font-bold text-emerald-400">
              {stats?.last_backup ? new Date(stats.last_backup).toLocaleDateString('ar-IQ') : 'لم يتم'}
            </p>
            <p className="text-sm text-slate-400">آخر نسخة</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 border-amber-500/30">
          <CardContent className="p-4 text-center">
            <Zap className={`w-8 h-8 mx-auto mb-2 ${stats?.auto_backup_enabled ? 'text-amber-500' : 'text-slate-500'}`} />
            <p className={`text-lg font-bold ${stats?.auto_backup_enabled ? 'text-amber-400' : 'text-slate-400'}`}>
              {stats?.auto_backup_enabled ? 'مفعّل' : 'معطّل'}
            </p>
            <p className="text-sm text-slate-400">النسخ التلقائي</p>
          </CardContent>
        </Card>
      </div>

      {/* الإعدادات */}
      {showSettings && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5" />
              إعدادات النسخ الاحتياطي
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* النسخ التلقائي */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-900/50">
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-white font-medium">النسخ الاحتياطي التلقائي</p>
                  <p className="text-sm text-slate-400">إنشاء نسخ احتياطية تلقائياً</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.auto_backup_enabled}
                  onChange={(e) => setConfig({...config, auto_backup_enabled: e.target.checked})}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
              </label>
            </div>

            {config.auto_backup_enabled && (
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">التكرار</label>
                  <select
                    value={config.backup_frequency}
                    onChange={(e) => setConfig({...config, backup_frequency: e.target.value})}
                    className="w-full bg-slate-700/50 border border-slate-600 text-white rounded-lg px-4 py-2"
                  >
                    <option value="daily">يومياً</option>
                    <option value="weekly">أسبوعياً</option>
                    <option value="monthly">شهرياً</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">وقت النسخ</label>
                  <Input
                    type="time"
                    value={config.backup_time}
                    onChange={(e) => setConfig({...config, backup_time: e.target.value})}
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">الاحتفاظ (أيام)</label>
                  <Input
                    type="number"
                    value={config.retention_days}
                    onChange={(e) => setConfig({...config, retention_days: parseInt(e.target.value)})}
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
              </div>
            )}

            {/* البيانات المشمولة */}
            <div>
              <p className="text-white font-medium mb-3">البيانات المشمولة في النسخة:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { key: 'include_products', label: 'المنتجات' },
                  { key: 'include_customers', label: 'الزبائن' },
                  { key: 'include_suppliers', label: 'الموردين' },
                  { key: 'include_sales', label: 'المبيعات' },
                  { key: 'include_purchases', label: 'المشتريات' },
                  { key: 'include_expenses', label: 'المصروفات' },
                  { key: 'include_employees', label: 'الموظفين' },
                  { key: 'include_settings', label: 'الإعدادات' },
                ].map((item) => (
                  <label key={item.key} className="flex items-center gap-2 p-3 rounded-lg bg-slate-900/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config[item.key]}
                      onChange={(e) => setConfig({...config, [item.key]: e.target.checked})}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-slate-300">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSettings(false)} className="border-slate-600">
                <X className="w-4 h-4 me-2" />
                إلغاء
              </Button>
              <Button onClick={saveConfig} className="bg-emerald-500 hover:bg-emerald-600">
                <Save className="w-4 h-4 me-2" />
                حفظ الإعدادات
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* قائمة النسخ الاحتياطية */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <FileJson className="w-5 h-5 text-emerald-500" />
            النسخ الاحتياطية المحفوظة
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-4" />
              <p className="text-slate-400">جاري التحميل...</p>
            </div>
          ) : backups.length === 0 ? (
            <div className="text-center py-12">
              <Database className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl text-white mb-2">لا توجد نسخ احتياطية</h3>
              <p className="text-slate-400 mb-4">ابدأ بإنشاء أول نسخة احتياطية لبياناتك</p>
              <Button onClick={createBackup} className="bg-emerald-500 hover:bg-emerald-600">
                <Play className="w-4 h-4 me-2" />
                إنشاء نسخة الآن
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map((backup) => (
                <div
                  key={backup.id}
                  className="p-4 rounded-lg bg-slate-900/50 border border-slate-700 hover:border-emerald-500/50 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl bg-blue-500/20">
                        <FileJson className="w-6 h-6 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-white font-medium">نسخة احتياطية</p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(backup.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <HardDrive className="w-4 h-4" />
                            {formatBytes(backup.size_bytes || 0)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Database className="w-4 h-4" />
                            {backup.total_records || 0} سجل
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${backup.status === 'completed' ? 'bg-emerald-500' : 'bg-amber-500'} text-white`}>
                        {backup.status === 'completed' ? 'مكتمل' : 'قيد المعالجة'}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadBackup(backup.id)}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteBackup(backup.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* تنبيه الأمان */}
      <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 border-amber-500/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-amber-500 mt-1" />
            <div>
              <p className="text-white font-medium">نصيحة أمان</p>
              <p className="text-sm text-slate-400 mt-1">
                يُنصح بتحميل نسخة احتياطية وحفظها في مكان آمن خارج التطبيق بشكل دوري. 
                هذا يضمن عدم فقدان بياناتك في أي ظرف.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
